import { useState, useMemo, useCallback, useEffect } from 'react'
import styled from '@emotion/styled'
import AddIcon from '@mui/icons-material/Add'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import makeStyles from '@mui/styles/makeStyles'
import { func, string, array, bool, object, oneOfType } from 'prop-types'
import { DefaultRow } from './default-row'
import { EnhancedTableHeader } from './enhanced-table-header'
import { EnhancedTableToolbar } from './enhanced-table-toolbar'
import { t } from '@lingui/core/macro'

function desc(a, b, orderBy) {
  let order = 0
  if (a[orderBy] === null || a[orderBy] == undefined) {
    order = 1
  } else if (b[orderBy] === null || b[orderBy] == undefined) {
    order = -1
  } else if (typeof a[orderBy] === 'string') {
    if (b[orderBy].toLowerCase() < a[orderBy].toLowerCase()) {
      order = -1
    } else if (b[orderBy].toLowerCase() > a[orderBy].toLowerCase()) {
      order = 1
    }
  } else if (b[orderBy] < a[orderBy]) {
    order = -1
  } else if (b[orderBy] > a[orderBy]) {
    order = 1
  }
  return order
}

function stableSort(arr, cmp) {
  const stabilizedThis = arr.map((el, index) => [el, index])
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0])
    if (order !== 0) return order
    return a[1] - b[1]
  })
  return stabilizedThis.map(el => el[0])
}

function getSorting(order, orderBy) {
  return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy)
}

const styles = makeStyles(() => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '5px',
    maxWidth: '100%',
  },
  paper: {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  table: {
    // minWidth: 750,
    maxWidth: '100%',
  },
  tableWrapper: {
    // overflow: 'scroll',
    overflow: 'hidden',
  },
}))

const Base = styled.div`
  background-color: white;
`

const MarginTop = styled.div`
  margin-top: 20px;
`

const ColumnFlex = styled.div`
  padding: 20px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const NOOP = () => {}
const defaultFilterChange = (filter, rows) =>
  rows.filter(row =>
    filter && filter.length > 0 ? row.name.toLowerCase().match(filter.toLowerCase()) : true,
  )

export const EnhancedTable = ({
  order: initialOrder = 'asc',
  orderBy: initialOrderBy = '',
  rows,
  columns,
  handleRowClick = NOOP,
  keyField,
  entity = '',
  noAdd = false,
  addItem = NOOP,
  deleteItems: onDeleteItems = NOOP,
  editItems: onEditItems = NOOP,
  duplicateItem: onDuplicateItem = NOOP,
  onReset: reset = NOOP,
  blockMultipleEdit = false,
  emptyStateMessage = '',
  onFilterChange = defaultFilterChange,
  HeaderComponent = EnhancedTableHeader,
  ToolbarComponent = EnhancedTableToolbar,
  FooterComponent = null,
  RowComponent = DefaultRow,
}) => {
  const classes = styles()
  const [order, setOrder] = useState(initialOrder)
  const [orderBy, setOrderBy] = useState(initialOrderBy)
  const [selected, setSelected] = useState([])
  const [filteredRows, setFilteredRows] = useState(rows)
  const [pagination, setPagination] = useState(() => r => r)
  const [search, setSearch] = useState('')

  useEffect(() => setFilteredRows(rows), [rows, setFilteredRows])

  const handleRequestSort = useCallback(
    (event, property, index) => {
      const isDesc = orderBy === columns[index].lookup && order === 'desc'
      setOrder(isDesc ? 'asc' : 'desc')
      setOrderBy(columns[index].lookup)
    },
    [setOrder, setOrderBy, columns, order, orderBy],
  )

  const handleSelectAllClick = useCallback(
    event => {
      if (event.target.checked) {
        const newSelecteds = rows.map(n => n[keyField])
        setSelected(newSelecteds)
        return
      }
      setSelected([])
    },
    [keyField, rows],
  )

  const filterChanged = useCallback(
    value => {
      setSearch(value)
      setFilteredRows(onFilterChange(value, rows))
    },
    [onFilterChange, rows],
  )

  const filteredAndPaginatedRows = useMemo(
    () => pagination(stableSort(filteredRows, getSorting(order, orderBy))),
    [order, orderBy, pagination, filteredRows],
  )

  const editItems = useCallback(() => {
    onEditItems(
      rows.filter(row => {
        return selected.indexOf(row[keyField]) !== -1
      }),
    )
  }, [keyField, onEditItems, rows, selected])

  const deleteItems = useCallback(() => {
    onDeleteItems(
      rows.filter(row => {
        return selected.indexOf(row[keyField]) !== -1
      }),
    )
  }, [keyField, onDeleteItems, rows, selected])

  const duplicateItem = useCallback(() => {
    onDuplicateItem(
      rows.filter(row => {
        return selected.indexOf(row[keyField]) !== -1
      }),
    )
  }, [keyField, onDuplicateItem, rows, selected])

  const onReset = useCallback(() => {
    reset(
      rows.filter(row => {
        return selected.indexOf(row[keyField]) !== -1
      }),
    )
  }, [keyField, reset, rows, selected])

  const onPageChange = useCallback(fxn => setPagination(() => fxn), [setPagination])

  return (
    <Base>
      <ToolbarComponent
        numSelected={selected.length}
        filterChanged={filterChanged}
        addItem={addItem}
        editItems={editItems}
        deleteItems={deleteItems}
        noAdd={noAdd}
        onReset={onReset}
        term={search}
        blockMultipleEdit={blockMultipleEdit}
        duplicateItem={duplicateItem}
        resultsDrawer
      />
      <Table className={classes.table} aria-labelledby="tableTitle" size="medium">
        <HeaderComponent
          numSelected={selected.length}
          order={order}
          orderBy={orderBy}
          onSelectAllClick={handleSelectAllClick}
          onRequestSort={handleRequestSort}
          rowCount={filteredRows.length}
          columns={columns}
        />
        <TableBody>
          {filteredAndPaginatedRows.map((row, index) => {
            const isItemSelected = selected.includes(row[keyField])
            const labelId = `enhanced-table-checkbox-${index}`

            return (
              <RowComponent
                key={`${row.id || row.dataset_id}-${index}`}
                labelId={labelId}
                isItemSelected={isItemSelected}
                handleClick={handleRowClick}
                columns={columns}
                row={row}
                keyField={keyField}
              />
            )
          })}
        </TableBody>
      </Table>

      {!filteredRows.length && (
        <Container>
          <ColumnFlex>
            <div style={{ alignItems: 'center' }}>{emptyStateMessage}</div>
            <MarginTop>
              {!noAdd && (
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  onClick={addItem}
                >
                  <AddIcon />
                  {t`Add ${entity}`}
                </Button>
              )}
            </MarginTop>
          </ColumnFlex>
        </Container>
      )}
      {FooterComponent && (
        <FooterComponent pageSize={5} onPageChange={onPageChange} rows={filteredRows} />
      )}
    </Base>
  )
}

EnhancedTable.propTypes = {
  handleRowClick: func,
  order: string,
  orderBy: string,
  rows: array.isRequired,
  columns: array.isRequired,
  keyField: string.isRequired,
  entity: string,
  noAdd: bool,
  addItem: func,
  deleteItems: func,
  editItems: func,
  duplicateItem: func,
  onReset: func,
  blockMultipleEdit: bool,
  emptyStateMessage: oneOfType([string, object]),
  HeaderComponent: func,
  onFilterChange: func,
  ToolbarComponent: func,
  FooterComponent: func,
  RowComponent: func,
}
